/**
 * RSA helpers for `/tools/crypto/rsa-keygen` and `/tools/crypto/rsa`.
 *
 * All work runs through `window.crypto.subtle`:
 *   - generateKeys(): RSA-OAEP / RSA-PSS / RSASSA-PKCS1-v1_5 key pair
 *     + PKCS#8 (private) / SPKI (public) DER → PEM serialisation.
 *   - encrypt / decrypt: RSA-OAEP only (PKCS#1-v1_5 padding is not
 *     exposed by SubtleCrypto and is discouraged for new code).
 *   - sign / verify: RSA-PSS or RSASSA-PKCS1-v1_5, hash 256/384/512.
 *
 * Plaintext is treated as UTF-8; ciphertext / signatures travel as
 * base64. Throws Error subclasses with friendly messages on every
 * known failure mode (bad PEM, mismatched usage, oversize input).
 */

export type RsaScheme = 'oaep' | 'pss' | 'pkcs1';
export type RsaHash = 'SHA-256' | 'SHA-384' | 'SHA-512';
export type RsaModulusBits = 2048 | 3072 | 4096;

export interface KeyPairPem {
  privatePem: string;
  publicPem: string;
}

export interface GenerateOpts {
  scheme: RsaScheme;
  hash: RsaHash;
  bits: RsaModulusBits;
}

const ALG: Record<RsaScheme, string> = {
  oaep: 'RSA-OAEP',
  pss: 'RSA-PSS',
  pkcs1: 'RSASSA-PKCS1-v1_5',
};

/* ------------------------------------------------------------------ */
/* Key generation                                                     */
/* ------------------------------------------------------------------ */

export async function generateRsaKeys(opts: GenerateOpts): Promise<KeyPairPem> {
  const subtle = subtleOrThrow();
  const algorithmName = ALG[opts.scheme];
  // public exponent 65537 (0x010001) is the universal default and the
  // only value SubtleCrypto accepts.
  const publicExponent = new Uint8Array([0x01, 0x00, 0x01]);

  const usage: KeyUsage[] =
    opts.scheme === 'oaep' ? ['encrypt', 'decrypt'] : ['sign', 'verify'];

  const pair = await subtle.generateKey(
    {
      name: algorithmName,
      modulusLength: opts.bits,
      publicExponent: publicExponent as BufferSource,
      hash: opts.hash,
    } as RsaHashedKeyGenParams,
    true,
    usage,
  );

  const pkcs8 = await subtle.exportKey('pkcs8', (pair as CryptoKeyPair).privateKey);
  const spki = await subtle.exportKey('spki', (pair as CryptoKeyPair).publicKey);

  return {
    privatePem: derToPem(pkcs8, 'PRIVATE KEY'),
    publicPem: derToPem(spki, 'PUBLIC KEY'),
  };
}

/* ------------------------------------------------------------------ */
/* Encrypt / decrypt (OAEP)                                           */
/* ------------------------------------------------------------------ */

export async function rsaEncrypt(
  publicKeyPem: string,
  plaintext: string,
  hash: RsaHash = 'SHA-256',
): Promise<string> {
  const subtle = subtleOrThrow();
  const key = await importPublicKey(publicKeyPem, 'oaep', hash, ['encrypt']);
  const data = new TextEncoder().encode(plaintext);
  const cipher = await subtle.encrypt({ name: 'RSA-OAEP' }, key, data);
  return bufferToBase64(cipher);
}

export async function rsaDecrypt(
  privateKeyPem: string,
  ciphertextB64: string,
  hash: RsaHash = 'SHA-256',
): Promise<string> {
  const subtle = subtleOrThrow();
  const key = await importPrivateKey(privateKeyPem, 'oaep', hash, ['decrypt']);
  const buf = base64ToBuffer(ciphertextB64);
  const plain = await subtle.decrypt({ name: 'RSA-OAEP' }, key, buf);
  return new TextDecoder().decode(plain);
}

/* ------------------------------------------------------------------ */
/* Sign / verify                                                      */
/* ------------------------------------------------------------------ */

export async function rsaSign(
  privateKeyPem: string,
  message: string,
  scheme: 'pss' | 'pkcs1',
  hash: RsaHash,
): Promise<string> {
  const subtle = subtleOrThrow();
  const key = await importPrivateKey(privateKeyPem, scheme, hash, ['sign']);
  const data = new TextEncoder().encode(message);
  const params =
    scheme === 'pss'
      ? ({ name: 'RSA-PSS', saltLength: hashSaltLen(hash) } as RsaPssParams)
      : ({ name: 'RSASSA-PKCS1-v1_5' } as Algorithm);
  const sig = await subtle.sign(params, key, data);
  return bufferToBase64(sig);
}

export async function rsaVerify(
  publicKeyPem: string,
  message: string,
  signatureB64: string,
  scheme: 'pss' | 'pkcs1',
  hash: RsaHash,
): Promise<boolean> {
  const subtle = subtleOrThrow();
  const key = await importPublicKey(publicKeyPem, scheme, hash, ['verify']);
  const data = new TextEncoder().encode(message);
  const sig = base64ToBuffer(signatureB64);
  const params =
    scheme === 'pss'
      ? ({ name: 'RSA-PSS', saltLength: hashSaltLen(hash) } as RsaPssParams)
      : ({ name: 'RSASSA-PKCS1-v1_5' } as Algorithm);
  return subtle.verify(params, key, sig, data);
}

/* ------------------------------------------------------------------ */
/* PEM <-> DER                                                        */
/* ------------------------------------------------------------------ */

export function derToPem(buf: ArrayBuffer, label: string): string {
  const b64 = bufferToBase64(buf);
  const lines: string[] = [];
  for (let i = 0; i < b64.length; i += 64) {
    lines.push(b64.slice(i, i + 64));
  }
  return `-----BEGIN ${label}-----\n${lines.join('\n')}\n-----END ${label}-----\n`;
}

export function pemToDer(pem: string, expectLabel: RegExp = /-----BEGIN ([A-Z ]+)-----/): ArrayBuffer {
  const m = pem.match(expectLabel);
  if (!m) throw new Error('PEM 头无法识别（期待 -----BEGIN ...----- 起始）');
  const stripped = pem
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s+/g, '');
  if (stripped.length === 0) throw new Error('PEM 内容为空');
  return base64ToBuffer(stripped);
}

/* ------------------------------------------------------------------ */
/* Internal helpers                                                   */
/* ------------------------------------------------------------------ */

function subtleOrThrow(): SubtleCrypto {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('当前环境缺失 window.crypto.subtle，请用现代浏览器。');
  }
  return crypto.subtle;
}

async function importPublicKey(
  pem: string,
  scheme: RsaScheme,
  hash: RsaHash,
  usage: KeyUsage[],
): Promise<CryptoKey> {
  const der = pemToDer(pem, /-----BEGIN PUBLIC KEY-----/);
  return crypto.subtle.importKey(
    'spki',
    der,
    { name: ALG[scheme], hash } as RsaHashedImportParams,
    false,
    usage,
  );
}

async function importPrivateKey(
  pem: string,
  scheme: RsaScheme,
  hash: RsaHash,
  usage: KeyUsage[],
): Promise<CryptoKey> {
  const der = pemToDer(pem, /-----BEGIN PRIVATE KEY-----/);
  return crypto.subtle.importKey(
    'pkcs8',
    der,
    { name: ALG[scheme], hash } as RsaHashedImportParams,
    false,
    usage,
  );
}

function hashSaltLen(hash: RsaHash): number {
  switch (hash) {
    case 'SHA-256':
      return 32;
    case 'SHA-384':
      return 48;
    case 'SHA-512':
      return 64;
  }
}

export function bufferToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

export function base64ToBuffer(b64: string): ArrayBuffer {
  const cleaned = b64.replace(/\s+/g, '');
  const binary = atob(cleaned);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out.buffer;
}
